import { useMemo, useState } from "react";
import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { gatherHours, calculateHours, type PayrollAssignment } from "src/components/payroll/payroll";
import { useAuthStore, User } from "src/components/main/authStore";
import {
    round,
    numberReducer,
    onlyUnique,
    objAscSort,
    boolAndReducer,
} from "src/components/common/functions";
import type { HoursAssignment, HoursLoaderData } from "./actions_loaders";

type HoursDaySummary = {
    regular: number;
    ot1: number;
    ot2: number;
    travel: number;
};

type HoursDayAssignments = (HoursAssignment & HoursDaySummary)[];

function toPayrollAssignment(assignment: HoursAssignment): PayrollAssignment {
    return {
        ...assignment,
        id: assignment.assignmentId,
    };
}

export default function Hours() {
    const { assignments, week, weeks } = useLoaderData() as HoursLoaderData;
    const user = useAuthStore(state => state.user) as User;
    const navigate = useNavigate();
    const [filterWords, setFilterWords] = useState("");

    const allData = useMemo<HoursDayAssignments[]>(() => {
        const currentWeekAssignments = assignments
            .map(assignment => ({
                ...assignment,
                travelStartTime: assignment.travelStartTime.replace(/-/g, "/"),
                workStartTime: assignment.workStartTime.replace(/-/g, "/"),
                workEndTime: assignment.workEndTime.replace(/-/g, "/"),
                travelEndTime: assignment.travelEndTime.replace(/-/g, "/"),
            }))
            .filter(assignment => assignment.travelStartTime.substring(0, 4) !== "1900");

        const groupedByDate = currentWeekAssignments
            .map(assignment => assignment.travelStartTime.substring(0, 10))
            .filter(onlyUnique)
            .map(date =>
                currentWeekAssignments.filter(assignment => assignment.travelStartTime.substring(0, 10) === date)
            );

        return groupedByDate.map(dayAssignments => {
            const sortedAssignments = [...dayAssignments].sort((a, b) => objAscSort(a, b, "startTime"));
            const gathered = sortedAssignments.map(assignment => gatherHours(toPayrollAssignment(assignment), user.unionName));
            const totalHours = gathered.map(item => item.reduce(numberReducer, 0)).reduce(numberReducer, 0);
            const summary = calculateHours(
                totalHours - gathered.map(item => item[1]).reduce(numberReducer, 0) - gathered.map(item => item[2]).reduce(numberReducer, 0),
                gathered.map(item => item[1]).reduce(numberReducer, 0),
                gathered.map(item => item[2]).reduce(numberReducer, 0),
                Date.parse(sortedAssignments[0].travelStartTime.replace(/-/g, "/")),
                Date.parse(sortedAssignments[0].travelEndTime.replace(/-/g, "/")),
                user.unionName,
                user.email,
                sortedAssignments[0].laborCategory === "Travel",
                user.department
            );

            return sortedAssignments.map(assignment => ({
                ...assignment,
                regular: summary[0],
                ot1: summary[1],
                ot2: summary[2],
                travel: summary[3],
            }));
        });
    }, [assignments, user.department, user.email, user.unionName]);

    const filteredData = useMemo(
        () =>
            allData.filter(dayAssignments =>
                filterWords
                    .toLowerCase()
                    .split(" ")
                    .filter(Boolean)
                    .map(word => JSON.stringify(dayAssignments).toLowerCase().includes(word))
                    .reduce(boolAndReducer, true)
            ),
        [allData, filterWords]
    );

    return (
        <div className="cssTab">
            <nav className="my-2">
                <label htmlFor="searchBar">Search</label>
                <input
                    id="searchBar"
                    className="form-control inline w-auto ms-2 me-3"
                    type="text"
                    value={filterWords}
                    onChange={event => {
                        setFilterWords(event.currentTarget.value.toLowerCase());
                    }}
                />
                <label htmlFor="week">Week</label>
                <select
                    id="week"
                    className="form-control inline w-auto ms-2"
                    value={week}
                    onChange={event => {
                        navigate(`/Hours/${event.currentTarget.value}`);
                    }}
                >
                    {weeks.map(renderedWeek => (
                        <option key={renderedWeek} value={renderedWeek}>
                            {renderedWeek}
                        </option>
                    ))}
                </select>
            </nav>
            <div className="cssContent pb8rem">
                {!filteredData.length ? (
                    <div className="cssLoading">Empty</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table">
                            <thead className="noTableTopBorder">
                                <tr>
                                    <th>Date</th>
                                    <th>Project Name</th>
                                    <th>Calculated Regular Hours</th>
                                    <th>Calculated OT-1.5 Hours</th>
                                    <th>Calculated OT-2 Hours</th>
                                    <th>Calculated Travel Hours</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((dayAssignments, dayIndex) => (
                                    <tr key={dayIndex}>
                                        <td>
                                            <div className="text-center">{dayAssignments[0].travelStartTime.substring(0, 10)}</div>
                                            {dayAssignments[0].travelStartTime.substring(0, 10) !==
                                            dayAssignments[0].travelEndTime.substring(0, 10) ? (
                                                <div className="text-center">
                                                    <span className="badge badge-info">overnight</span>
                                                </div>
                                            ) : null}
                                        </td>
                                        <td>
                                            {dayAssignments.map((assignment, assignmentIndex) => (
                                                <li key={assignmentIndex}>
                                                    <Link to={`/Assignments/${assignment.assignmentId}`}>{assignment.projectName}</Link>
                                                </li>
                                            ))}
                                        </td>
                                        <td>{round(dayAssignments[0].regular, 2)}</td>
                                        <td>{round(dayAssignments[0].ot1, 2)}</td>
                                        <td>{round(dayAssignments[0].ot2, 2)}</td>
                                        <td>{round(dayAssignments[0].travel, 2)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td>
                                        <strong>Total</strong>
                                    </td>
                                    <td></td>
                                    <th>{round(filteredData.map(dayAssignments => dayAssignments[0].regular).reduce(numberReducer, 0), 2)}</th>
                                    <th>{round(filteredData.map(dayAssignments => dayAssignments[0].ot1).reduce(numberReducer, 0), 2)}</th>
                                    <th>{round(filteredData.map(dayAssignments => dayAssignments[0].ot2).reduce(numberReducer, 0), 2)}</th>
                                    <th>{round(filteredData.map(dayAssignments => dayAssignments[0].travel).reduce(numberReducer, 0), 2)}</th>
                                </tr>
                            </tbody>
                        </table>
                        <div className="text-danger m-2">* Note: this may not be the final result.</div>
                    </div>
                )}
            </div>
        </div>
    );
}
